import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Flex,
  Box,
  Button,
  Input,
  Spinner,
  Textarea,
  Image,
  Checkbox,
  DialogRoot,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
  DialogPositioner,
  Portal,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { LuTrash2, LuPlus, LuSearch, LuX } from 'react-icons/lu';
import { useAuth } from '../hooks/useAuth';
import Header from '../components/Header';
import {
  searchStockImages,
  getCategories,
  generateStockImages,
  deleteStockImage,
  type StockImage,
} from '../api/stockImages';

const ADMIN_EMAIL = 'andiskim@gmail.com';
const PAGE_SIZE = 50;
const DEBOUNCE_MS = 400;

const AdminStockImages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Data
  const [images, setImages] = useState<StockImage[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Generate modal
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [generateInput, setGenerateInput] = useState(`{
  "items": [
    {
      "name": "Margherita Pizza",
      "category": "food",
      "tags": ["pizza", "italian"]
    }
  ]
}`);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Image preview modal
  const [previewImage, setPreviewImage] = useState<StockImage | null>(null);

  // Infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null);

  const hasMore = images.length < total;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchInput);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
    setImages([]);
  }, [debouncedQuery, selectedCategory]);

  const fetchImages = useCallback(async (pageNum: number, append: boolean) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    try {
      const params: Record<string, string | number> = {
        page: pageNum,
        page_size: PAGE_SIZE,
      };
      if (debouncedQuery.trim()) params.query = debouncedQuery.trim();
      if (selectedCategory) params.category = selectedCategory;

      const data = await searchStockImages(params);
      if (append) {
        setImages((prev) => [...prev, ...data.images]);
      } else {
        setImages(data.images);
      }
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load stock images:', error);
      toast.error('Failed to load stock images');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [debouncedQuery, selectedCategory]);

  // Fetch when page or filters change
  useEffect(() => {
    fetchImages(page, page > 1);
  }, [page, fetchImages]);

  // Infinite scroll observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !isLoadingMore) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore]);

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const data = await getCategories();
        setCategories(data);
      } catch {
        // optional
      }
    };
    fetchCats();
  }, []);

  // Auth guard
  if (!user || user.email !== ADMIN_EMAIL) {
    navigate('/', { replace: true });
    return null;
  }

  const hasActiveFilters = searchInput !== '' || selectedCategory !== '';

  const handleResetFilters = () => {
    setSearchInput('');
    setDebouncedQuery('');
    setSelectedCategory('');
  };

  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category === selectedCategory ? '' : category);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === images.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(images.map((img) => img.id)));
    }
  };

  const handleDeleteSelected = async (ids?: Set<string>) => {
    const toDelete = ids || selectedIds;
    if (toDelete.size === 0) return;
    setIsDeleting(true);
    try {
      await Promise.all(Array.from(toDelete).map((id) => deleteStockImage(id)));
      toast.success(`Deleted ${toDelete.size} image(s)`);
      setSelectedIds(new Set());
      // Refetch from page 1
      setPage(1);
      setImages([]);
      fetchImages(1, false);
    } catch (error) {
      console.error('Failed to delete images:', error);
      toast.error('Failed to delete some images');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGenerate = async () => {
    let parsed;
    try {
      parsed = JSON.parse(generateInput);
    } catch {
      toast.error('Invalid JSON');
      return;
    }

    const items = parsed.items || parsed;
    if (!Array.isArray(items) || items.length === 0) {
      toast.error('Expected an array of items or an object with an "items" array');
      return;
    }

    // Prompt priority: input field > JSON-level prompt
    const prompt = generatePrompt.trim() || parsed.prompt || undefined;

    setIsGenerating(true);
    try {
      const result = await generateStockImages(items, prompt);
      toast.success(`Submitted ${result.submitted} image(s) for generation`);
      setIsGenerateOpen(false);
      setGenerateInput('');
      setGeneratePrompt('');
      // Images appear async, refresh after a delay
      setTimeout(() => {
        setPage(1);
        setImages([]);
        fetchImages(1, false);
      }, 5000);
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to generate images';
      toast.error(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Flex minHeight="100vh" direction="column" backgroundColor="neutral.900">
      <Header
        actions={
          <Flex gap="8px" alignItems="center">
            {selectedIds.size > 0 && (
              <Button
                onClick={() => handleDeleteSelected()}
                loading={isDeleting}
                height="fit-content"
                py="6px"
                px="12px"
                fontWeight="500"
                fontSize="13px"
                color="white"
                backgroundColor="error.600"
                _hover={{ backgroundColor: 'error.800' }}
              >
                <Flex alignItems="center" gap="6px">
                  <LuTrash2 size={14} />
                  Delete ({selectedIds.size})
                </Flex>
              </Button>
            )}
            <Button
              onClick={() => setIsGenerateOpen(true)}
              height="fit-content"
              py="6px"
              px="12px"
              fontWeight="500"
              fontSize="13px"
              color="neutral.900"
              backgroundColor="neutral.100"
              _hover={{ backgroundColor: 'neutral.200' }}
            >
              <Flex alignItems="center" gap="6px">
                <LuPlus style={{ width: 14, height: 14 }} />
                Generate
              </Flex>
            </Button>
          </Flex>
        }
      />

      {/* Content */}
      <Flex flex="1" direction="column" p={{ base: '16px', md: '24px' }} overflow="auto" alignItems="center">
        <Box width="100%" maxWidth="960px">
          {/* Filters row */}
          <Flex
            mb="16px"
            gap="10px"
            alignItems="center"
            flexWrap="wrap"
          >
            {/* Category pills - left side */}
            {categories.length > 0 && (
              <Flex gap="6px" flexWrap="wrap">
                {categories.map((cat) => (
                  <Button
                    key={cat}
                    onClick={() => handleCategoryFilter(cat)}
                    height="fit-content"
                    py="6px"
                    px="14px"
                    fontWeight="400"
                    fontSize="14px"
                    color={selectedCategory === cat ? 'neutral.900' : 'neutral.400'}
                    backgroundColor={selectedCategory === cat ? 'neutral.100' : 'transparent'}
                    borderWidth="1px"
                    borderColor={selectedCategory === cat ? 'neutral.100' : 'neutral.700'}
                    borderRadius="8px"
                    _hover={{
                      borderColor: selectedCategory === cat ? 'neutral.100' : 'neutral.500',
                      color: selectedCategory === cat ? 'neutral.900' : 'neutral.300',
                    }}
                  >
                    {cat}
                  </Button>
                ))}
              </Flex>
            )}

            {/* Reset filters */}
            {hasActiveFilters && (
              <Button
                onClick={handleResetFilters}
                height="fit-content"
                py="4px"
                px="10px"
                fontWeight="400"
                fontSize="13px"
                color="neutral.500"
                backgroundColor="transparent"
                borderWidth="0"
                _hover={{ color: 'neutral.300' }}
              >
                <Flex alignItems="center" gap="4px">
                  <LuX size={12} />
                  Reset
                </Flex>
              </Button>
            )}

            {/* Search input - right side */}
            <Flex minWidth="200px" maxWidth="300px" position="relative" ml="auto">
              <Input
                placeholder="Search images..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                p="8px 12px"
                pl="36px"
                fontWeight="400"
                fontSize="14px"
                color="neutral.300"
                borderWidth="1px"
                borderColor="neutral.700"
                backgroundColor="neutral.800"
                borderRadius="8px"
                _focus={{ borderColor: 'neutral.600', outline: 'none' }}
              />
              <Box
                position="absolute"
                left="12px"
                top="50%"
                transform="translateY(-50%)"
                color="neutral.500"
                pointerEvents="none"
              >
                <LuSearch size={14} />
              </Box>
            </Flex>
          </Flex>

          {isLoading ? (
            <Flex justifyContent="center" alignItems="center" py="80px">
              <Spinner size="xl" color="neutral.300" />
            </Flex>
          ) : images.length === 0 ? (
            <Flex
              direction="column"
              justifyContent="center"
              alignItems="center"
              gap="16px"
              py="80px"
            >
              <Box fontSize="18px" fontWeight="500" color="neutral.100">
                No images found
              </Box>
              <Box fontSize="14px" color="neutral.400">
                {hasActiveFilters
                  ? 'Try adjusting your search or filters'
                  : 'Generate some stock images to get started'}
              </Box>
            </Flex>
          ) : (
            <>
              {/* Table */}
              <Box
                borderWidth="1px"
                borderColor="neutral.700"
                borderRadius="8px"
                overflow="hidden"
              >
                {/* Table Header */}
                <Flex
                  px="16px"
                  py="10px"
                  backgroundColor="neutral.800"
                  borderBottomWidth="1px"
                  borderColor="neutral.700"
                  alignItems="center"
                  gap="16px"
                >
                  <Box width="32px" flexShrink={0}>
                    <Checkbox.Root
                      checked={selectedIds.size === images.length && images.length > 0}
                      onCheckedChange={toggleSelectAll}
                      size="sm"
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control
                        borderColor="neutral.600"
                        _checked={{ backgroundColor: 'neutral.100', borderColor: 'neutral.100' }}
                      />
                    </Checkbox.Root>
                  </Box>
                  <Box width="80px" flexShrink={0} fontSize="12px" fontWeight="500" color="neutral.500">
                    Image
                  </Box>
                  <Box flex="1" fontSize="12px" fontWeight="500" color="neutral.500">
                    Name
                  </Box>
                  <Box width="100px" fontSize="12px" fontWeight="500" color="neutral.500" display={{ base: 'none', md: 'block' }}>
                    Category
                  </Box>
                  <Box flex="1" fontSize="12px" fontWeight="500" color="neutral.500" display={{ base: 'none', lg: 'block' }}>
                    Tags
                  </Box>
                  <Box width="140px" fontSize="12px" fontWeight="500" color="neutral.500" display={{ base: 'none', md: 'block' }}>
                    Created
                  </Box>
                  <Box width="36px" flexShrink={0} />
                </Flex>

                {/* Table Rows */}
                {images.map((img) => (
                  <Flex
                    key={img.id}
                    px="16px"
                    py="8px"
                    alignItems="center"
                    gap="16px"
                    borderBottomWidth="1px"
                    borderColor="neutral.700"
                    _hover={{ backgroundColor: 'neutral.800' }}
                    _last={{ borderBottomWidth: '0px' }}
                  >
                    <Box width="32px" flexShrink={0}>
                      <Checkbox.Root
                        checked={selectedIds.has(img.id)}
                        onCheckedChange={() => toggleSelect(img.id)}
                        size="sm"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control
                          borderColor="neutral.600"
                          _checked={{ backgroundColor: 'neutral.100', borderColor: 'neutral.100' }}
                        />
                      </Checkbox.Root>
                    </Box>
                    <Box
                      width="80px"
                      height="60px"
                      flexShrink={0}
                      borderRadius="4px"
                      overflow="hidden"
                      cursor="pointer"
                      onClick={() => setPreviewImage(img)}
                    >
                      <Image
                        src={img.url}
                        alt={img.name}
                        width="100%"
                        height="100%"
                        objectFit="cover"
                      />
                    </Box>
                    <Box
                      flex="1"
                      fontSize="14px"
                      fontWeight="400"
                      color="neutral.200"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {img.name}
                    </Box>
                    <Box
                      width="100px"
                      fontSize="13px"
                      fontWeight="400"
                      color="neutral.400"
                      display={{ base: 'none', md: 'block' }}
                    >
                      {img.category}
                    </Box>
                    <Box
                      flex="1"
                      fontSize="12px"
                      color="neutral.500"
                      display={{ base: 'none', lg: 'flex' }}
                      gap="4px"
                      flexWrap="wrap"
                      overflow="hidden"
                    >
                      {img.tags.map((tag) => (
                        <Box
                          key={tag}
                          px="6px"
                          py="1px"
                          backgroundColor="neutral.700"
                          borderRadius="4px"
                          fontSize="11px"
                          color="neutral.400"
                          whiteSpace="nowrap"
                        >
                          {tag}
                        </Box>
                      ))}
                    </Box>
                    <Box
                      width="140px"
                      fontSize="12px"
                      fontWeight="400"
                      color="neutral.500"
                      display={{ base: 'none', md: 'block' }}
                    >
                      {new Date(img.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Box>
                    <Flex
                      width="36px"
                      flexShrink={0}
                      justifyContent="center"
                      alignItems="center"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSelected(new Set([img.id]));
                      }}
                    >
                      <Box
                        as="button"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width="28px"
                        height="28px"
                        borderRadius="6px"
                        color="error.600"
                        cursor="pointer"
                        _hover={{ backgroundColor: 'neutral.700' }}
                      >
                        <LuTrash2 style={{ width: 14, height: 14 }} />
                      </Box>
                    </Flex>
                  </Flex>
                ))}
              </Box>

              {/* Infinite scroll sentinel */}
              <Box ref={sentinelRef} height="1px" />

              {isLoadingMore && (
                <Flex justifyContent="center" py="24px">
                  <Spinner size="md" color="neutral.400" />
                </Flex>
              )}
            </>
          )}
        </Box>
      </Flex>

      {/* Image Preview Modal */}
      <DialogRoot open={!!previewImage} onOpenChange={(e) => !e.open && setPreviewImage(null)} placement="center">
        <Portal>
        <DialogBackdrop bg="blackAlpha.600" />
        <DialogPositioner>
        <DialogContent
          backgroundColor="neutral.800"
          borderWidth="1px"
          borderColor="neutral.700"
          borderRadius="8px"
          maxW="640px"
          p={{ base: '20px', md: '24px' }}
        >
          <DialogHeader pb="0">
            <DialogTitle fontSize="18px" fontWeight="500" color="neutral.100">
              {previewImage?.name}
            </DialogTitle>
            <DialogCloseTrigger color="neutral.400" />
          </DialogHeader>
          <DialogBody py="16px">
            {previewImage && (
              <Flex direction="column" gap="16px">
                <Box borderRadius="8px" overflow="hidden">
                  <Image
                    src={previewImage.url}
                    alt={previewImage.name}
                    width="100%"
                    borderRadius="8px"
                  />
                </Box>
                <Flex gap="16px" flexWrap="wrap">
                  <Flex direction="column" gap="2px">
                    <Box fontSize="12px" color="neutral.500">Category</Box>
                    <Box fontSize="14px" color="neutral.300">{previewImage.category}</Box>
                  </Flex>
                  <Flex direction="column" gap="2px">
                    <Box fontSize="12px" color="neutral.500">Size</Box>
                    <Box fontSize="14px" color="neutral.300">
                      {previewImage.width} x {previewImage.height}
                    </Box>
                  </Flex>
                  <Flex direction="column" gap="2px">
                    <Box fontSize="12px" color="neutral.500">Created</Box>
                    <Box fontSize="14px" color="neutral.300">
                      {new Date(previewImage.created_at).toLocaleString()}
                    </Box>
                  </Flex>
                </Flex>
                {previewImage.tags.length > 0 && (
                  <Flex direction="column" gap="4px">
                    <Box fontSize="12px" color="neutral.500">Tags</Box>
                    <Flex gap="6px" flexWrap="wrap">
                      {previewImage.tags.map((tag) => (
                        <Box
                          key={tag}
                          px="8px"
                          py="2px"
                          backgroundColor="neutral.700"
                          borderRadius="4px"
                          fontSize="13px"
                          color="neutral.300"
                        >
                          {tag}
                        </Box>
                      ))}
                    </Flex>
                  </Flex>
                )}
              </Flex>
            )}
          </DialogBody>
          <DialogFooter gap="8px">
            <Button
              onClick={() => {
                if (previewImage) {
                  const id = previewImage.id;
                  setPreviewImage(null);
                  handleDeleteSelected(new Set([id]));
                }
              }}
              height="fit-content"
              py="6px"
              px="12px"
              fontWeight="500"
              fontSize="13px"
              color="error.600"
              backgroundColor="transparent"
              borderWidth="1px"
              borderColor="neutral.700"
              _hover={{ backgroundColor: 'neutral.700' }}
            >
              <Flex alignItems="center" gap="6px">
                <LuTrash2 size={14} />
                Delete
              </Flex>
            </Button>
          </DialogFooter>
        </DialogContent>
        </DialogPositioner>
        </Portal>
      </DialogRoot>

      {/* Generate Modal */}
      <DialogRoot open={isGenerateOpen} onOpenChange={(e) => !e.open && setIsGenerateOpen(false)} placement="center">
        <Portal>
        <DialogBackdrop bg="blackAlpha.600" />
        <DialogPositioner>
        <DialogContent
          backgroundColor="neutral.800"
          borderWidth="1px"
          borderColor="neutral.700"
          borderRadius="8px"
          maxW="560px"
          p={{ base: '20px', md: '24px' }}
        >
          <DialogHeader pb="0">
            <DialogTitle fontSize="18px" fontWeight="500" color="neutral.100">
              Generate Stock Images
            </DialogTitle>
            <DialogCloseTrigger color="neutral.400" />
          </DialogHeader>
          <DialogBody py="16px">
            <Flex direction="column" gap="16px">
              <Box fontSize="14px" color="neutral.400" lineHeight="150%">
                Paste a JSON array of items. Each item needs a <strong>name</strong>, and optionally{' '}
                <strong>category</strong> and <strong>tags</strong>.
              </Box>
              <Flex direction="column" gap="8px">
                <Box fontSize="14px" fontWeight="400" color="neutral.300">
                  Items JSON
                </Box>
                <Textarea
                  value={generateInput}
                  onChange={(e) => setGenerateInput(e.target.value)}
                  fontFamily="monospace"
                  fontSize="13px"
                  color="neutral.300"
                  backgroundColor="neutral.900"
                  borderWidth="1px"
                  borderColor="neutral.700"
                  borderRadius="8px"
                  p="12px"
                  minHeight="200px"
                  _focus={{ borderColor: 'neutral.600', outline: 'none' }}
                />
              </Flex>
              <Flex direction="column" gap="8px">
                <Flex justifyContent="space-between" alignItems="baseline">
                  <Box fontSize="14px" fontWeight="400" color="neutral.300">
                    Prompt template
                  </Box>
                  <Box fontSize="12px" fontWeight="400" color="neutral.500">
                    Optional
                  </Box>
                </Flex>
                <Input
                  value={generatePrompt}
                  onChange={(e) => setGeneratePrompt(e.target.value)}
                  placeholder="A top-down photograph of {item} on a white marble surface."
                  p="10px"
                  fontWeight="400"
                  fontSize="14px"
                  color="neutral.300"
                  borderWidth="1px"
                  borderColor="neutral.700"
                  backgroundColor="neutral.900"
                  borderRadius="8px"
                  _focus={{ borderColor: 'neutral.600', outline: 'none' }}
                />
                <Box fontSize="12px" color="neutral.500" lineHeight="150%">
                  Use <strong>{'{item}'}</strong> as a placeholder for the item name. Applies to all items.
                </Box>
              </Flex>
            </Flex>
          </DialogBody>
          <DialogFooter gap="8px">
            <Button
              onClick={() => setIsGenerateOpen(false)}
              height="fit-content"
              py="8px"
              px="16px"
              fontWeight="500"
              fontSize="14px"
              color="neutral.300"
              backgroundColor="transparent"
              borderWidth="1px"
              borderColor="neutral.600"
              _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.700' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              loading={isGenerating}
              height="fit-content"
              py="8px"
              px="16px"
              fontWeight="500"
              fontSize="14px"
              color="neutral.900"
              backgroundColor="neutral.100"
              _hover={{ backgroundColor: 'neutral.200' }}
            >
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
        </DialogPositioner>
        </Portal>
      </DialogRoot>
    </Flex>
  );
};

export default AdminStockImages;
